<script setup>
import {computed, nextTick, onMounted, reactive, ref} from "vue";
import YTTablePagination from "@/components/YTTablePagination/YTTablePagination.vue";
import router from "@/router/index.js";
import { listBicycleDeviceCodes, exportQrcodeBicycleDeviceCodes, getQrcodeBicycleDeviceCodes, generateBicycleDeviceCodes, exportBicycleDeviceCodes, bindBicycleDeviceCodes, removeBicycleDeviceCodes } from "@/api/bicycleDeviceCodes/bicycleDeviceCodes.js"
import {listBicycles} from "@/api/bicycles/bicycles.js";
import {Search} from "@element-plus/icons-vue";
import moment from "moment";

/*搜索功能*/
// 表单数据
const formData = reactive({
  deviceCode: "",
  crTime: []
})

/*表格数据展示*/
// 翻页组件锚点
const YTTablePaginationRef = ref()
// 表格加载状态
const loading = ref(false)
// 当前选择的标签卡
const tab = ref(null)

// 表格headers
const headers = [
  { title: "二维码编号", key: "deviceCode", align: "center", nowrap: true },
  { title: "绑定车辆", key: "bicycle", align: "center", nowrap: true },
  { title: "绑定状态", key: "bound", align: "center", nowrap: true },
  { title: "二维码生成时间", key: "crTime", align: "center", nowrap: true },
  { title: "操作", key: "controls", align: "center", minWidth: "160px", fixed: true },
]

// 表格数据
const tableData = ref([])
// 翻页总条数
const total = ref(0)

// 根据绑定状态获取文本类型
const getBindStatusTextType = (status) => {
  if (status) return "success"
  if (status === false) return "warning"
  return "default"
}
// 处理文件流下载
const downloadBlob = (data, filename) => {
  const blob = new Blob([data])
  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = downloadUrl
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  setTimeout(() => {
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }, 100)
}

/*二维码批量生成弹窗*/
// 弹窗开关
const openCreateQRCodeDialog = ref(false)
// 弹窗表单锚点
const createQRCodeDialogFormRef = ref()
// 弹窗表单数据
const createQRCodeDialogForm = reactive({
  qrCodePrefix: "",
  qrCodeStartNo: null,
  qrCodeSerialLength: null,
  qrCodeQuantity: null,
})
// 校验二维码起始数
const validateQRCodeStartNo = (rule, value, callback) => {
  if (value === null || value === undefined || value === "") {
    callback()
    return
  }
  if (!Number.isInteger(value) || value < 0) {
    callback(new Error("二维码起始数只能为非负整数"))
    return
  }
  if (createQRCodeDialogForm.qrCodeSerialLength) {
    const maxStartNumber = Math.pow(10, createQRCodeDialogForm.qrCodeSerialLength) - 1
    if (value > maxStartNumber) {
      callback(new Error(`二维码起始数不能超过${maxStartNumber}`))
      return
    }
  }
  callback()
}
// 校验二维码号码长度
const validateQRCodeSerialLength = (rule, value, callback) => {
  if (value === null || value === undefined || value === "") {
    callback()
    return
  }
  if (!Number.isInteger(value) || value < 2 || value > 4) {
    callback(new Error("二维码号码长度只能为2到4的整数"))
    return
  }
  if (createQRCodeDialogForm.qrCodeStartNo !== null && createQRCodeDialogForm.qrCodeStartNo !== undefined) {
    const maxStartNumber = Math.pow(10, value) - 1
    if (createQRCodeDialogForm.qrCodeStartNo > maxStartNumber) {
      callback(new Error(`当前长度下二维码起始数不能超过${maxStartNumber}`))
      return
    }
  }
  callback()
}
// 弹窗表单校验规则
const createQRCodeDialogRules = {
  qrCodePrefix: [
    { required: true, message: "请输入二维码前缀", trigger: "blur" },
    { pattern: /^[A-Za-z0-9]+$/, message: "二维码前缀只能包含字母、数字", trigger: "blur" }
  ],
  qrCodeStartNo: [
    { required: true, message: "请输入二维码起始数", trigger: "change" },
    { validator: validateQRCodeStartNo, trigger: "change" }
  ],
  qrCodeSerialLength: [
    { required: true, message: "请输入二维码号码长度", trigger: "change" },
    { validator: validateQRCodeSerialLength, trigger: "change" }
  ],
  qrCodeQuantity: [
    { required: true, message: "请输入生成数量", trigger: "change" }
  ],
}

/*单个绑定车辆弹窗*/
// 弹窗开关
const openBindBicycleDialog = ref(false)
// 当前绑定二维码信息
const currentBindQRCode = ref(null)
// 车辆列表加载状态
const bindBicycleLoading = ref(false)
// 车辆表格锚点
const bindBicycleTableRef = ref()
// 车辆查询表单
const bindBicycleFormData = reactive({
  brandName: null,
  model: null,
})
// 车辆列表分页信息
const bindBicyclePage = reactive({
  page: 1,
  pageSize: 5,
})
// 车辆列表数据
const bindBicycleTableData = ref([])
// 车辆列表总数
const bindBicycleTotal = ref(0)
// 已选择车辆
const selectedBindBicycle = ref(null)
// 车辆列表展示起始条数
const bindBicyclePageStart = computed(() => {
  if (!bindBicycleTotal.value) return 0
  return (bindBicyclePage.page - 1) * bindBicyclePage.pageSize + 1
})
// 车辆列表展示结束条数
const bindBicyclePageEnd = computed(() => {
  return Math.min(bindBicyclePage.page * bindBicyclePage.pageSize, bindBicycleTotal.value)
})

// 清空绑定车辆勾选
const clearBindBicycleSelection = async () => {
  selectedBindBicycle.value = null
  await nextTick()
  bindBicycleTableRef.value?.clearSelection()
}

// 获取车辆列表
const getBindBicycleList = async () => {
  try {
    bindBicycleLoading.value = true
    await clearBindBicycleSelection()
    const page = {
      page: bindBicyclePage.page,
      pageSize: bindBicyclePage.pageSize,
    }
    const param = {
      brandName: bindBicycleFormData.brandName,
      model: bindBicycleFormData.model,
      viewType: "BRAND",
    }
    const res = await listBicycles(page, param)
    bindBicycleTableData.value = res.list || []
    bindBicycleTotal.value = parseInt(res.total) || 0
  } catch (e) {
    console.log(e)
  } finally {
    bindBicycleLoading.value = false
  }
}

// 判断车辆是否可选
const canSelectBindBicycle = (row) => {
  return !selectedBindBicycle.value || selectedBindBicycle.value?.bicycleId === row.bicycleId
}

// 处理车辆勾选变化
const handleBindBicycleSelectionChange = async (selection) => {
  if (selection.length > 1) {
    const current = selection[selection.length - 1]
    bindBicycleTableRef.value?.clearSelection()
    await nextTick()
    bindBicycleTableRef.value?.toggleRowSelection(current, true)
    selectedBindBicycle.value = current
    return
  }
  selectedBindBicycle.value = selection[0] || null
}

// 搜索绑定车辆列表
const handleSearchBindBicycle = () => {
  bindBicyclePage.page = 1
  getBindBicycleList()
}

// 重置绑定车辆列表查询
const handleResetBindBicycle = () => {
  bindBicycleFormData.brandName = null
  bindBicycleFormData.model = null
  handleSearchBindBicycle()
}

// 处理绑定车辆列表翻页
const handleBindBicyclePageChange = (page) => {
  bindBicyclePage.page = page
  getBindBicycleList()
}

// 清空绑定车辆弹窗缓存
const resetBindBicycleDialog = () => {
  bindBicycleFormData.brandName = null
  bindBicycleFormData.model = null
  bindBicyclePage.page = 1
  bindBicycleTableData.value = []
  bindBicycleTotal.value = 0
  clearBindBicycleSelection()
}

/*操作功能*/
// 处理生成二维码
const handleCreateQRCode = () => {
  resetCreateQRCodeDialogForm()
  openCreateQRCodeDialog.value = true
}
// 处理导出二维码图片
const handleExportQRCode = async () => {
  try {
    loading.value = true
    const param = {
      deviceCode: formData.deviceCode || null,
      startDate: formData.crTime?.[0] ? moment(formData.crTime?.[0]).format("YYYY-MM-DD") : null,
      endDate: formData.crTime?.[1] ? moment(formData.crTime?.[1]).format("YYYY-MM-DD") : null,
    }
    if (tab.value !== 0) {
      param.bound = tab.value
    }
    const res = await exportQrcodeBicycleDeviceCodes(param)
    downloadBlob(res.data, "二维码.zip")
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}
// 处理导出表单
const handleExportList = async () => {
  try {
    loading.value = true
    const param = {
      deviceCode: formData.deviceCode || null,
      startDate: formData.crTime?.[0] ? moment(formData.crTime?.[0]).format("YYYY-MM-DD") : null,
      endDate: formData.crTime?.[1] ? moment(formData.crTime?.[1]).format("YYYY-MM-DD") : null,
    }
    if (tab.value !== 0) {
      param.bound = tab.value
    }
    const res = await exportBicycleDeviceCodes(param)
    downloadBlob(res.data, "二维码表单.xlsx")
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}
// 处理下载二维码
const handleDownload = async (item) => {
  try {
    loading.value = true
    const res = await getQrcodeBicycleDeviceCodes({
      deviceCode: item.deviceCode
    })
    downloadBlob(res.data, `${item.deviceCode}.png`)
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}
// 处理查看详情
const handleDetail = (item) => {
  router.push({
    path: "/detailBicycleQRCode",
    query: {
      bicycleDeviceCodeId: item.bicycleDeviceCodeId
    }
  })
}
// 处理解绑二维码
const handleBindChange = async (item) => {
  if (!item?.bicycleDeviceCodeId) return ElMessage.error("二维码信息获取失败")
  try {
    loading.value = true
    await removeBicycleDeviceCodes(item.bicycleDeviceCodeId)
    ElMessage.success("解绑成功")
    await handleSearch()
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}
// 处理打开绑定车辆弹窗
const handleBindQRCode = (item) => {
  currentBindQRCode.value = item
  resetBindBicycleDialog()
  openBindBicycleDialog.value = true
  getBindBicycleList()
}
// 处理批量绑定车辆
const handleBatchBindBicycle = () => {
  router.push({
    path: "/bicycleQRCodeBind"
  })
}
// 处理关闭二维码批量生成弹窗
const resetCreateQRCodeDialogForm = () => {
  createQRCodeDialogForm.qrCodePrefix = ""
  createQRCodeDialogForm.qrCodeStartNo = null
  createQRCodeDialogForm.qrCodeSerialLength = null
  createQRCodeDialogForm.qrCodeQuantity = null
  createQRCodeDialogFormRef.value?.clearValidate()
}
// 处理关闭二维码批量生成弹窗
const handleCloseCreateQRCodeDialog = () => {
  openCreateQRCodeDialog.value = false
  resetCreateQRCodeDialogForm()
}
// 处理确认只生成二维码
const handleOnlyCreateQRCode = () => {
  createQRCodeDialogFormRef.value.validate(async (valid) => {
    if (!valid) return
    try {
      loading.value = true
      const param = {
        prefix: createQRCodeDialogForm.qrCodePrefix,
        startNumber: createQRCodeDialogForm.qrCodeStartNo,
        quantity: createQRCodeDialogForm.qrCodeQuantity,
        serialLength: createQRCodeDialogForm.qrCodeSerialLength
      }
      await generateBicycleDeviceCodes(param)
      ElMessage.success("生成成功")
      handleCloseCreateQRCodeDialog()
      await handleSearch("btnSearch")
    } catch (e) {
      console.log(e)
    } finally {
      loading.value = false
    }
  })
}
// 处理确认并同时绑定车辆
const handleCreateAndBindBicycle = async () => {
  await handleOnlyCreateQRCode()
  router.push({
    path: "/bicycleQRCodeBind"
  })
}
// 处理关闭绑定车辆弹窗
const handleCloseBindBicycleDialog = () => {
  openBindBicycleDialog.value = false
  currentBindQRCode.value = null
  resetBindBicycleDialog()
}
// 处理确认绑定车辆
const handleConfirmBindBicycle = async () => {
  if (!selectedBindBicycle.value) return ElMessage.error("请选择绑定车辆")
  if (!currentBindQRCode.value?.bicycleDeviceCodeId) return ElMessage.error("二维码信息获取失败")
  try {
    bindBicycleLoading.value = true
    const param = {
      bicycleId: selectedBindBicycle.value.bicycleId,
      bicycleDeviceCodeId: currentBindQRCode.value.bicycleDeviceCodeId,
    }
    await bindBicycleDeviceCodes(param)
    ElMessage.success("绑定成功")
    handleCloseBindBicycleDialog()
    handleSearch()
  } catch (e) {
    console.log(e)
  } finally {
    bindBicycleLoading.value = false
  }
}
// 根据表单搜索信息
const handleSearch = async (mode = 'init') => {
  if (mode === 'btnSearch') YTTablePaginationRef.value?.resetPage()
  try {
    const page = {
      page: YTTablePaginationRef.value.getPage(),
      pageSize: YTTablePaginationRef.value.getPageSize()
    }
    loading.value = true
    const param = {
      deviceCode: formData.deviceCode,
      startDate: formData.crTime?.[0] ? moment(formData.crTime?.[0]).format("YYYY-MM-DD") : null,
      endDate: formData.crTime?.[1] ? moment(formData.crTime?.[1]).format("YYYY-MM-DD") : null,
    }
    if (tab.value !== 0) {
      param.bound = tab.value
    }
    const res = await listBicycleDeviceCodes(page, param)
    tableData.value = []
    tableData.value = res.list || []
    total.value = parseInt(res.total)
    loading.value = false
  } catch (e) {
    console.log(e)
    loading.value = false
  }
}
// 翻页
const handleChangePage = () => {
  handleSearch()
}

// 清空表单内容
const handleReset = () => {
  formData.deviceCode = ""
  formData.crTime = []
  handleSearch('btnSearch')
}

// 处理切换标签卡
const handleTabChange = () => {
  YTTablePaginationRef.value?.resetPage()
  handleSearch()
}

onMounted(() => {
  handleSearch()
})
</script>

<template>
  <div class="container_box mg_b_2 qr-toolbar">
    <div class="flex_left">
      <v-btn color="success" @click="handleCreateQRCode">生成二维码</v-btn>
    </div>
  </div>

  <div class="container_box mg_b_2 qr-search-box">
    <el-form :model="formData" class="qr-search-form" label-position="left">
      <el-form-item label="二维码编号：">
        <el-input
            v-model="formData.deviceCode"
            class="qr-code-input"
            placeholder="二维码编号"
            clearable
        />
      </el-form-item>
      <el-form-item label="二维码生成时间：">
        <el-date-picker
            v-model="formData.crTime"
            class="qr-date-picker"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
        />
      </el-form-item>
      <div class="qr-search-actions">
        <v-btn color="success" size="large" @click="handleSearch('btnSearch')">搜索</v-btn>
        <v-btn size="large" @click="handleReset">重置</v-btn>
      </div>
    </el-form>
  </div>

  <div class="container_box qr-table-box">
    <v-row justify="center">
      <v-col cols="11">
        <div class="qr-table-tools">
          <v-tabs
              v-model="tab"
              color="primary"
              @update:modelValue="handleTabChange"
          >
            <v-tab :value="null">全部</v-tab>
            <v-tab :value="true">已绑定</v-tab>
            <v-tab :value="false">未绑定</v-tab>
          </v-tabs>
          <div class="qr-export-actions">
            <v-btn v-if="tab === false" color="primary" @click="handleBatchBindBicycle">绑定车辆</v-btn>
            <v-btn color="success" @click="handleExportQRCode">导出二维码</v-btn>
            <v-btn color="success" @click="handleExportList">导出表单</v-btn>
          </div>
        </div>

        <v-data-table
            :headers="headers"
            :items="tableData"
            :loading="loading"
            :items-per-page="20"
            :no-data-text="'没有更多数据了'"
            hover
        >
          <template v-slot:loading>
            <v-skeleton-loader type="table-row@3"></v-skeleton-loader>
          </template>

          <template v-slot:item.bound="{ item }">
            <el-text :type="getBindStatusTextType(item.bound)">
              {{ item.bound === null ? "未知状态" : item.bound ? "已绑定" : "未绑定" }}
            </el-text>
          </template>

          <template v-slot:item.bicycle="{ item }">
            <div v-if="item?.bicycle">{{ item?.bicycle?.brandName ? item?.bicycle?.brandName : "—" }}{{ item?.bicycle?.model ? `-${item?.bicycle?.model}` : "—" }}{{ item?.bicycle?.color ? `-${item?.bicycle?.color}` : "—" }}</div>
            <div v-else>—</div>
          </template>

          <template v-slot:item.controls="{ item }">
            <div class="qr-controls">
              <el-link type="primary" :underline="false" @click="handleDownload(item)">下载</el-link>
              <span class="qr-control-divider">|</span>
              <el-link type="primary" :underline="false" @click="handleDetail(item)">详情</el-link>
              <span class="qr-control-divider">|</span>
              <el-popconfirm
                  v-if="item.bound"
                  @confirm="handleBindChange(item)"
                  :title="`确定解绑二维码“${item.deviceCode}”吗？`"
              >
                <template #reference>
                  <el-link type="primary" :underline="false">解绑</el-link>
                </template>
                <template #actions="{ confirm, cancel }">
                  <div class="flex_bt">
                    <v-btn size="small" @click="cancel">取消</v-btn>
                    <v-btn size="small" color="#796CB4" @click="confirm">确定</v-btn>
                  </div>
                </template>
              </el-popconfirm>
              <el-link
                  v-else
                  type="primary"
                  :underline="false"
                  @click="handleBindQRCode(item)"
              >
                绑定
              </el-link>
            </div>
          </template>

          <template v-slot:bottom>
            <YTTablePagination
                ref="YTTablePaginationRef"
                :total="total"
                @page-change="handleChangePage"
            ></YTTablePagination>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>

  <el-dialog
      v-model="openBindBicycleDialog"
      width="1040"
      class="bind-bicycle-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
  >
    <template #header>
      <div class="bind-bicycle-dialog-title">选择车辆</div>
    </template>
    <div class="bind-bicycle-dialog-container">
      <div class="bind-bicycle-search">
        <el-input
            v-model="bindBicycleFormData.brandName"
            class="bind-bicycle-search-input"
            placeholder="车辆品牌"
            clearable
            @keyup.enter="handleSearchBindBicycle"
        />
        <el-input
            v-model="bindBicycleFormData.model"
            class="bind-bicycle-search-input"
            placeholder="车辆型号"
            clearable
            @keyup.enter="handleSearchBindBicycle"
        />
        <el-button
            class="bind-bicycle-search-button"
            :icon="Search"
            circle
            @click="handleSearchBindBicycle"
        />
      </div>

      <el-table
          ref="bindBicycleTableRef"
          class="bind-bicycle-table"
          :data="bindBicycleTableData"
          v-loading="bindBicycleLoading"
          row-key="bicycleId"
          border
          height="250"
          @selection-change="handleBindBicycleSelectionChange"
      >
        <el-table-column
            type="selection"
            width="48"
            :selectable="canSelectBindBicycle"
        />
        <el-table-column prop="brandName" label="车辆品牌" min-width="110" />
        <el-table-column prop="model" label="车辆型号" min-width="110" />
        <el-table-column prop="color" label="车辆颜色" min-width="90" />
        <el-table-column prop="ageRange" label="车辆使用年龄段" min-width="130" />
        <el-table-column prop="conditionLevel" label="车辆新旧程度" min-width="120" />
        <el-table-column prop="providerSponsor" label="所属公司/品牌方" min-width="150" />
        <el-table-column prop="currentVenue" label="车辆当前放置场地" min-width="160" />
        <el-table-column prop="placementDate" label="车辆放置时间" min-width="120" />
      </el-table>

      <div class="bind-bicycle-pagination">
        <span>显示{{ bindBicyclePageStart }}-{{ bindBicyclePageEnd }}条，共{{ bindBicycleTotal }}条</span>
        <el-pagination
            v-model:current-page="bindBicyclePage.page"
            :page-size="bindBicyclePage.pageSize"
            :total="bindBicycleTotal"
            layout="prev, pager, next"
            @current-change="handleBindBicyclePageChange"
        />
      </div>
    </div>
    <template #footer>
      <div class="bind-bicycle-dialog-footer">
        <v-btn variant="elevated" @click="handleCloseBindBicycleDialog">取消</v-btn>
        <v-btn class="mg_l_4" color="primary" variant="elevated" @click="handleConfirmBindBicycle">确认绑定</v-btn>
      </div>
    </template>
  </el-dialog>

  <el-dialog
      v-model="openCreateQRCodeDialog"
      width="520"
      style="padding: 20px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false">
    <template #header>
      <div class="yt_dialog_title" style="padding-bottom: 30px">二维码批量生成</div>
    </template>
    <div class="yt_dialog_container">
      <el-form
          ref="createQRCodeDialogFormRef"
          style="max-width: 385px;"
          :model="createQRCodeDialogForm"
          :rules="createQRCodeDialogRules"
          class="demo-ruleForm"
          status-icon
          id="createQRCodeDialogForm"
      >
        <el-form-item label="二维码前缀：" prop="qrCodePrefix">
          <el-input
              v-model="createQRCodeDialogForm.qrCodePrefix"
              style="width: 271px"
              maxlength="3"
              show-word-limit
              placeholder="最多3位，只能包含字母、数字"
          />
        </el-form-item>
        <el-form-item label="二维码起始数：" prop="qrCodeStartNo">
          <el-input-number
              v-model="createQRCodeDialogForm.qrCodeStartNo"
              style="width: 271px"
              :min="0"
              :step="1"
              :precision="0"
              controls-position="right"
              placeholder="例如001"
          />
        </el-form-item>
        <el-form-item label="二维码号码长度：" prop="qrCodeSerialLength">
          <el-input-number
              v-model="createQRCodeDialogForm.qrCodeSerialLength"
              style="width: 271px"
              :min="2"
              :max="4"
              :step="1"
              :precision="0"
              controls-position="right"
              placeholder="输入长度，最长4位"
          />
        </el-form-item>
        <el-form-item label="输入生成数量：" prop="qrCodeQuantity">
          <div class="flex_left">
            <el-input-number
                v-model="createQRCodeDialogForm.qrCodeQuantity"
                style="width: 235px"
                :min="0"
                :step="1"
                :precision="0"
                controls-position="right"
                placeholder="请输入数量"
            />
            <span class="qr-dialog-unit">个</span>
          </div>
        </el-form-item>
      </el-form>
    </div>
    <template #footer>
      <div class="yt_dialog_footer">
        <v-btn variant="elevated" @click="handleCloseCreateQRCodeDialog">取消</v-btn>
        <v-btn class="mg_l_2" color="primary" variant="elevated" @click="handleOnlyCreateQRCode">确认只生成二维码</v-btn>
        <v-btn class="mg_l_2" color="primary" variant="elevated" @click="handleCreateAndBindBicycle">确认并同时绑定车辆</v-btn>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.qr-toolbar {
  padding: 24px 44px;
  text-align: left;
}

.qr-search-box {
  padding: 30px 44px;
  text-align: left;
}

.qr-search-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px 54px;
  width: 100%;
}

.qr-search-form :deep(.el-form-item) {
  margin: 0;
}

.qr-code-input {
  width: 230px;
}

.qr-date-picker {
  width: 460px;
}

.qr-search-actions {
  display: flex;
  gap: 24px;
  margin-left: auto;
}

.qr-table-box {
  min-height: 720px;
  padding-top: 24px;
}

.qr-table-tools {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.qr-export-actions {
  display: flex;
  gap: 4px;
  padding-bottom: 8px;
}

.qr-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.qr-control-divider {
  color: #8ea0ff;
}

.yt_dialog_title {
  padding: 20px 0 17px 33px;
  font-weight: 500;
  font-size: 19px;
  color: #272539;
  text-align: left;
}

/*弹窗内容*/
.yt_dialog_container {
  padding-left: 33px;
  text-align: left;
  ::v-deep(.el-select__wrapper),
  ::v-deep(.el-input__wrapper) {
    height: 34px;
    font-size: 12px;
    color: #6F717D;
    background: #FFFFFF;
    border-radius: 9px;
    border: 1px solid #E8E8E9;
  }
}

.yt_dialog_footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin: 0 33px 33px 0;
}

.qr-dialog-unit {
  margin-left: 8px;
  color: #272539;
}

.bind-bicycle-dialog-title {
  padding: 4px 0 10px 14px;
  font-weight: 600;
  font-size: 18px;
  color: #272539;
  text-align: left;
}

.bind-bicycle-dialog-container {
  padding: 8px 14px 0;
}

.bind-bicycle-search {
  display: flex;
  align-items: center;
  gap: 30px;
  margin-bottom: 24px;
}

.bind-bicycle-search-input {
  width: 232px;
}

.bind-bicycle-search-button {
  color: #8a8d94;
  font-size: 18px;
}

.bind-bicycle-table {
  width: 100%;
}

.bind-bicycle-table :deep(.el-table__header-wrapper .el-table-column--selection .el-checkbox) {
  display: none;
}

.bind-bicycle-table :deep(th.el-table__cell) {
  background: #f7f8fa;
  color: #272539;
  font-weight: 600;
}

.bind-bicycle-table :deep(.cell) {
  text-align: center;
  white-space: nowrap;
}

.bind-bicycle-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  color: #333333;
  font-size: 13px;
}

.bind-bicycle-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

</style>
